import { InvoiceStatusWidget } from './../invoice-status-widget-types';
import { WidgetConfiguration } from "./../../widget-configs/models/widget-configuration";
import { WidgetFilterInstance } from "./../../common/filter/reducers/filter.reducers";
import { groupBy, sum } from "@common/lang/lang";
import {
  AllCurrency,
  InvoiceStatusByAmount,
  InvoiceStatusByVolume,
  makeCurrencyFilter,
  makeStatusFilter,
} from "@feature/invoice-status/invoice-status-widget-types";
import * as filterSelectors from "@common/filter/selectors/filter.selectors";
import * as widgetConfigSelectors from "@widget-configs/selectors/widget-configs";
import { InvoiceStatusWidgetFilterFieldProvider } from "@feature/invoice-status/filter/invoice-status-widget-filter-field-provider";
import { IRow, ITable } from "@common/models/table";
import * as lodash from "lodash";
import { QueryBuilderConfigProvider } from "@common/filter/query-builder-config-provider";
import { RuleSet } from "ngx-angular-query-builder";
import { andFilters } from "@common/filter/filter-utils";
import { precisionRound } from "@common/lang/lang";
import { INITIAL_INSTANCE_STATE } from "@feature/invoice-status/reducers/invoice-status-widget";
import * as userSettings from "../../user-settings/selectors/user-settings";

import { createFeatureSelector, createSelector } from "@ngrx/store";

import * as fromInvoiceStatusWidget from "@feature/invoice-status/reducers/invoice-status-widget";
import { Currency } from '@currency/index';
import { StandardFilterStrategy } from '@common/filter/standard-filter-strategy';

export const selectInvoiceStatusWidgetsState = createFeatureSelector<
  fromInvoiceStatusWidget.State
>(fromInvoiceStatusWidget.invoiceStatusWidgetFeature);

const invoiceStatusProperty = "SystemInvoiceStatus";
const invoiceCurrencyProperty = "SystemCurrency";

const {
  selectIds,
  selectEntities,
  selectAll,
  selectTotal,
} = fromInvoiceStatusWidget.adapter.getSelectors(
  selectInvoiceStatusWidgetsState,
);

export const selectEntityByWidgetId = (widgetId: number) =>
  createSelector(
    selectEntities,
    (entities) => entities[widgetId] || ({ ...INITIAL_INSTANCE_STATE }),
  );

export const getWidgetLoading = (widgetId: number) =>
  createSelector(selectEntityByWidgetId(widgetId), (entity) => entity.loading);

export const getCurrentView = (widgetId: number) =>
  createSelector(
    selectEntityByWidgetId(widgetId),
    (entity) => entity.currentView,
  );

export const getCurrency = (widgetId: number) =>
  createSelector(selectEntityByWidgetId(widgetId), (entity) => entity.currency);

export const getDrillDownActive = (widgetId: number) =>
  createSelector(
    selectEntityByWidgetId(widgetId),
    (entity) => entity.drillDownActive,
  );

export const getDrillDownStatus = (widgetId: number) =>
  createSelector(
    selectEntityByWidgetId(widgetId),
    (entity) => entity.drillDownStatus,
  );

export const getDrillDownLabel = (widgetId: number) =>
  createSelector(
    selectEntityByWidgetId(widgetId),
    (entity) => entity.drillDownLabel,
  );

export const getDrillDownStatusFilter = (widgetId: number) =>
  createSelector([
    getDrillDownActive(widgetId),
    getDrillDownStatus(widgetId),
  ], (active: boolean, status: string) => {
    if (!active) return null;
    return makeStatusFilter(status) as RuleSet;
  });

const unfilteredData = (widgetId: number) =>
  createSelector(selectEntityByWidgetId(widgetId), (entity) => {
    if (entity.loading) return null;
    return entity.data;
  });

export const availableCurrencies = (widgetId: number) =>
  createSelector(
    unfilteredData(widgetId),
    (table) => {
      if (!table) return [];
      return <string[]>groupBy(table.Rows, invoiceCurrencyProperty);
    },
  );

const filterFieldProvider = (widgetId: number) =>
  createSelector([
    unfilteredData(widgetId),
    userSettings.getCurrentProduct,
  ], (table: ITable, product: string) =>
    new InvoiceStatusWidgetFilterFieldProvider(table, product));

const filteredData = (widgetId: number, widgetConfigId: number) =>
  createSelector(
    [
      unfilteredData(widgetId),
      filterFieldProvider(widgetId),
      filterSelectors.selectByWidgetId(widgetId),
      widgetConfigSelectors.selectLoading(),
      widgetConfigSelectors.selectEntityByWidgetConfigId(widgetConfigId),
      getCurrency(widgetId),
      getDrillDownStatusFilter(widgetId),
    ],
    (
      data: ITable,
      fieldProvider: InvoiceStatusWidgetFilterFieldProvider,
      filter: WidgetFilterInstance,
      widgetConfigLoading: boolean,
      widgetConfig: WidgetConfiguration,
      currency: Currency,
      drillDownInvoiceStatusFilter: RuleSet,
    ) => {
      if (widgetConfigLoading || !data) return null;

      const table = lodash.cloneDeep(data) as ITable;
      const userFilter: RuleSet = filter ? filter.filter : null;
      const sysAdminFilter: RuleSet = widgetConfig.filter;
      const currencyFilter: RuleSet = currency && currency != AllCurrency
        ? makeCurrencyFilter(currency)
        : null;
      const drillDownFilter: RuleSet = drillDownInvoiceStatusFilter;
      const allFilters = andFilters(
        userFilter,
        sysAdminFilter,
        currencyFilter,
        drillDownFilter,
      );

      const fs = new StandardFilterStrategy<IRow>(fieldProvider);
      const rows = fs.applyFilter(data.Rows, allFilters);

      table.Rows = rows;
      addTotalRow(table);
      return table;
    },
  );

const addTotalRow = (table: ITable) => {
  const colNames = ["GrossAmount", "NetAmount", "VatAmount"];
  const cols = table.Columns.filter((c) => colNames.indexOf(c.Name) !== -1).map(
    (col) => ({ name: col.Name, total: 0 })
  );
  if (cols.length === 0) return;

  table.Rows.map((row) => cols.map((c) => c.total += +row[c.name]));

  const totalRow: any = {};
  totalRow.isTotalRow = true;

  cols.map((c) => {
    c.total = precisionRound(c.total, 2);
    totalRow[c.name] = c.total;
  });
  table.Rows.push(totalRow);
  table.hasTotalRow = true;
};

const noTotalRows = (row: IRow) => !row.isTotalRow;

export const queryBuilderConfig = (widgetId: number) =>
  createSelector(
    filterFieldProvider(widgetId),
    (ffp) => {
      const configBuilder = new QueryBuilderConfigProvider(ffp);
      return configBuilder.getQueryBuilderConfig();
    },
  );

export const tableData = (widgetId: number, widgetConfigId: number) =>
  filteredData(widgetId, widgetConfigId);

export const grossAmountByStatus = (widgetId: number, widgetConfigId: number) =>
  createSelector(
    [
      selectEntityByWidgetId(widgetId),
      filteredData(widgetId, widgetConfigId),
    ],
    (entity: InvoiceStatusWidget, table: ITable) => {
      if (entity.loading || !table) return [];
      const t = groupBy(table.Rows.filter(noTotalRows), invoiceStatusProperty);
      const all: InvoiceStatusByAmount[] = [];
      for (const key in t) {
        const item: InvoiceStatusByAmount = {
          InvoiceStatus: key,
          Amount: sum(t[key], "SystemGrossAmount"),
        };
        all.push(item);
      }
      return all;
    },
  );

export const volumeByStatus = (widgetId: number, widgetConfigId: number) =>
  createSelector(
    [
      selectEntityByWidgetId(widgetId),
      filteredData(widgetId, widgetConfigId),
    ],
    (entity: InvoiceStatusWidget, table: ITable) => {
      if (entity.loading || !table) return [];
      const t = groupBy(table.Rows.filter(noTotalRows), invoiceStatusProperty);
      const all: InvoiceStatusByVolume[] = [];
      for (const key in t) {
        const item: InvoiceStatusByVolume = {
          InvoiceStatus: key,
          InvoiceCount: t[key].length,
        };
        all.push(item);
      }
      return all;
    },
  );
